import { GENRE_LIBRARY } from '../data/GenreLibrary.js';
import { SCALE_INTERVALS, CHORD_FORMULAS, PROGRESSION_TEMPLATES, FUNCTIONAL_SUBSTITUTIONS, GROOVE_TEMPLATES, RHYTHM_PATTERNS, NOTES, normalizeKey } from '../data/theoryConstants.js';
import { allocateRoles, adaptEvents, ABSTRACT_ROLES } from './RoleAllocator.js';
import { getMelodyStrategy, transformPattern, decorateMelody, generateSwarm, augmentRhythm, generateOstinato, generatePhasingPattern, generateAdditiveMotif, generateArpeggio, applyGrooveLocking } from './MelodyStrategies.js';
import { getBassStrategy } from './BassStrategies.js';
import { applyVoiceLeading, applyGenreVoicing, applyLegatoGlue } from './VoiceLeading.js';
import { applyOrchestralVoicing, applyHornFifths, applyPedalPoint } from './OrchestralLogic/VoicingEngine.js';
import { calculateTension, applyTensionToDynamics } from './OrchestralLogic/TensionEngine.js';
import { generateCounterLine } from './OrchestralLogic/CounterpointEngine.js';
import { getPercussionNote, generateTimpaniRoll, generateCymbalCrash, generateActionBattery, getTimpaniPitch } from './OrchestralLogic/PercussionLogic.js';
import { applySATBVoiceLeading } from './OrchestralLogic/SATBEngine.js';
import { MotifManager } from './OrchestralLogic/MotifManager.js';
import { applyPhrasing, applyKeyswitches } from './OrchestralLogic/ArticulationEngine.js';
import { suggestSectionKeys } from './OrchestralLogic/ModulationEngine.js';
import { generateLockedGroove } from './OrchestralLogic/CompingEngine.js';
import { generateDrumFill, generateTransition } from './OrchestralLogic/TransitionEngine.js';
import { generateStringOstinato, generateWoodwindRun, generateBrassStabs, generatePolyrhythm, generateMotifComping } from './OrchestralLogic/TextureEngine.js';
import { getDrumStrategy } from './DrumStrategies.js';
import { generateAleatoricCloud } from './OrchestralLogic/AleatoricEngine.js';
import { generateTempoMap } from './OrchestralLogic/TempoEngine.js';
import { Random } from './Random.js';
import { getSectionIntensityCurve, determineDynamicInstrumentation, selectEnsembleForGenre } from './OrchestralLogic/Orchestrator.js';
import { enforceHarmonicContext, HarmonyManager } from './HarmonyManager.js';
import { getSeededConfig } from './SeededConfig.js';
import { getInstrumentById } from '../data/InstrumentLibrary.js';
import { analyzeMelody } from './Analysis/CounterpointAnalysis.js';
import { generateTensionCurve } from './NarrativeEngine.js';
import { applyPhraseShaping, applyBreathConstraints, applyHairpinPhrasing, applyAgogicAccents, applyGenreHumanization } from './Humanizer.js';
import { applyStrum, applyGuitarVoicing } from './StrummingEngine.js';
import { applyGroove } from './GrooveEngine.js';
import { applyExpressionCurves } from './OrchestralLogic/ExpressionEngine.js';
import { applyGlobalPhysics } from './OrchestralLogic/DurationPhysics.js';
import { generateHarpPattern } from './OrchestralLogic/HarpEngine.js';
import { cullUnisonNotes, capPolyphony, optimizeProfile } from './OrchestralLogic/OptimizationEngine.js';
import { ParameterResolver } from './ParameterResolver.js';
import { INFLUENCE_WEIGHTS } from './constants.js';
import { DialogueManager } from './OrchestralLogic/DialogueManager.js';
import { createRegistry } from './RegistryUtils.js';
import { applyVerticalSlotting } from './OrchestralLogic/VerticalSlotting.js';
import { ScoringEngine } from './Analysis/ScoringEngine.js';
import { FidelityAnalyzer } from './Analysis/FidelityAnalyzer.js';
import { ArchetypeRegistry } from './Analysis/ArchetypeRegistry.js';
import { applyVelocityStrategy } from './StrategyResolver.js';
import { applyInstrumentBehaviors } from './OrchestralLogic/InstrumentBehaviors.js';

export class GenerationPipeline {
    constructor(musicEngineInstance) {
        this.engine = musicEngineInstance;
        this.random = null;
        this.lastHarmonySource = null; // Track genre changes
        this.fidelityAnalyzer = new FidelityAnalyzer();
    }

    execute(inputProfile, context) {
        console.log(`[GenerationPipeline] Starting execution...`);

        // --- Phase 1: Pre-Generation Optimization ---
        // Compensate for user parameter settings that conflict with genre fidelity.
        // We clone the profile to avoid mutating the UI state directly.
        let profile = optimizeProfile({ ...inputProfile });

        // Task 8-2: State Hygiene (Hard Reset)
        this.resetSession(context, profile);
        context.cache = context.cache || {};

        let attempts = 0;
        // Optimization Configuration (Overrides default)
        const maxAttempts = (context.optimization && context.optimization.maxAttempts) ? context.optimization.maxAttempts : 20;
        const targetThreshold = (context.optimization && context.optimization.targetThreshold) ? context.optimization.targetThreshold : 0.7;
        let bestResult = null;
        let bestScore = -1;
        let lastScore = 0;
        let bestConfig = null; // State Persistence for Hill Climbing
        const auditLog = [];

        // Lookup Archetype
        const archetype = ArchetypeRegistry.get(profile.harmonySource);
        const shouldOptimize = !!archetype;

        // Perform Initialization ONCE initially to set up traits, structure, etc.
        // If High Temp mutation (Seed Reroll) occurs, we will need to re-run parts of this.
        try {
             // Ensure random is set for Init
             context.random = context.random || new Random(profile.seed);
             this.stepInitialization(profile, context);
             this.random = context.random; // Sync local ref

             // Initialize Best Config with starting state
             if (context.config) {
                 bestConfig = JSON.parse(JSON.stringify(context.config));
             }
        } catch (e) {
             console.error("[GenerationPipeline] Initialization failed", e);
        }

        // Loop Logic: Success Threshold OR Safety Brake
        // Also skip loop if no archetype is found (Unknown Genre)
        let lastEvaluation = null;

        while (shouldOptimize && lastScore < targetThreshold && attempts < maxAttempts) {

            // --- Feedback Loop Mutation ---
            if (attempts > 0) {
                 // HILL CLIMBING LOGIC: Revert to Best Config if last attempt failed
                 // Only applies for Medium/Low temp (Vector Tweaks).
                 // High Temp (Seed Reroll) is a global reset, so we don't revert config there (it gets rebuilt).
                 let mutationScore = lastScore;

                 // RELAXED REVERSION: Only revert if score drop is significant (> 0.10).
                 // This allows the optimizer to explore "plateaus" or slightly worse valleys to find new peaks.
                 const TOLERANCE = 0.10;

                 if (lastScore < (bestScore - TOLERANCE) && lastScore >= 0.4) {
                     // Revert!
                     console.log(`[Optimization] Reverting to Best Config (Score: ${bestScore}) due to drop from ${lastScore}.`);
                     if (bestConfig) {
                         context.config = JSON.parse(JSON.stringify(bestConfig));
                     }
                     // Use bestScore to determine mutation magnitude (finer adjustments)
                     mutationScore = bestScore;
                 }

                 const mutationMsg = this.mutateContext(context, mutationScore, attempts, profile, lastEvaluation);
            }

            // Re-sync random just in case mutation changed it
            this.random = context.random;

            // VARIATION INJECTION: Re-Roll Genre Traits (Strategy Selection)
            // If we are retrying, try a different strategy permutation (e.g. 'riff' vs 'arpeggio')
            // This prevents getting stuck in a local optimum where the chosen strategy is the bottleneck.
            if (attempts > 0 && context.ensembleKey) {
                context.traits = this.engine.getGenreTraits(context.ensembleKey, profile.harmonySource);
                // Preserve forced roles if any (though they are part of traits, so re-getting is fine)
            }

            // --- ENHANCEMENT: Global Intensity Curve (Round 3 - Phase 2) ---
            // Re-generate if seed changed or just to be safe
            const globalDensity = (context.config && context.config.vectors) ? context.config.vectors.energy : 0.5;
            context.intensityCurve = getSectionIntensityCurve(context.structure, globalDensity, this.random);

            let chordProgression = [];
            let voicedChords = [];
            let allEvents = [];
            let tempoMap = [];

            // 2. Harmony Step
            try {
                chordProgression = this.stepHarmony(profile, context);
            } catch (e) {
                console.error("[GenerationPipeline] Harmony Generation failed", e);
                chordProgression = [];
            }

            // --- Defect Fix: Intro Safety Fallback ---
            if (chordProgression.length === 0) {
                 console.warn("[GenerationPipeline] Empty Progression. Generating Safety Pedal Point.");
                 const rootKey = context.rootKey || 'C';
                 const scaleType = context.scaleType || 'major';
                 let barOffset = 0;
                 context.structure.forEach(sect => {
                     const bars = sect.bars || 4;
                     const rootIdx = NOTES.indexOf(normalizeKey(rootKey));
                     const chord = {
                         degree: 0,
                         rootPitchClass: rootIdx,
                         quality: scaleType === 'minor' ? 'minor' : 'major',
                         type: 'triad',
                         notes: [rootIdx, (rootIdx + 4) % 12, (rootIdx + 7) % 12],
                         barIndex: barOffset,
                         duration: 4,
                         section: sect.type,
                         tension: 0.1
                     };
                     for(let b=0; b<bars; b++) {
                         chordProgression.push({ ...chord, barIndex: barOffset + b });
                     }
                     barOffset += bars;
                 });
            }

            if (chordProgression.length > 0) {
                // 3. Voicing Step
                try {
                    voicedChords = this.stepVoicing(chordProgression, profile, context);
                    this.validateAndRepairChords(voicedChords, context);
                } catch (e) {
                    console.error("[GenerationPipeline] Voicing Generation failed", e);
                    voicedChords = [];
                }

                // 4. Orchestration & Event Generation Step
                try {
                    if (voicedChords.length > 0) {
                        allEvents = this.stepOrchestration(voicedChords, profile, context);
                    }
                } catch (e) {
                    console.error("[GenerationPipeline] Orchestration Generation failed", e);
                    allEvents = [];
                }
            }

            // 5. Post-Processing & Cleanup
            try {
                allEvents = this.stepPostProcessing(allEvents, voicedChords, context, profile);

                // --- Defect Fix: Mix & Motion (CC Injection) ---
                try {
                    const ccEvents = applyExpressionCurves(allEvents, context);
                    allEvents = ccEvents;
                } catch (e) {
                    console.warn("[GenerationPipeline] Expression Engine failed", e);
                }

            } catch (e) {
                 console.error("[GenerationPipeline] Post-Processing failed", e);
            }

            // 6. Tempo Map
            try {
                const harmonyTraits = context.traits;
                tempoMap = generateTempoMap(context.structure, context.bpm || 120, {
                    humanize: profile.humanizeTempo,
                    rubatoIntensity: harmonyTraits ? harmonyTraits.rubatoIntensity : 0.0,
                    random: this.random,
                    getTension: context.getTension
                });
            } catch (e) {
                console.error("[GenerationPipeline] Tempo Map Generation failed", e);
                tempoMap = [];
            }

            // --- SCORING & OPTIMIZATION ---
            // Only score if events exist
            if (allEvents.length > 0) {
                let evaluation = ScoringEngine.evaluate({
                    events: allEvents,
                    progression: chordProgression,
                    structure: context.structure
                }, archetype);

                // --- INTEGRATION: Fidelity Audit (Soft Rules) ---
                // Only run if the structural foundation is decent (> 0.4)
                if (evaluation.score > 0.4) {
                    const fidelityContext = { ...context, instruments: context.instruments };
                    const fidelityReport = this.fidelityAnalyzer.audit(allEvents, fidelityContext, archetype);

                    let fidelityScore = 1.0;
                    const criticalIssues = [];

                    // Scan for critical bottlenecks in any instrument
                    Object.values(fidelityReport.instruments).forEach(inst => {
                        if (inst.issues.length > 0) {
                            inst.issues.forEach(issue => {
                                // Deduct 0.1 per critical issue, up to 0.5 max penalty
                                fidelityScore -= 0.1;
                                criticalIssues.push(`${inst.role}:${issue}`);
                            });
                        }
                    });

                    fidelityScore = Math.max(0.5, fidelityScore); // Floor at 0.5

                    // Weighted Composite Score
                    // Foundation (Scoring) 60%, Vibe (Fidelity) 40%
                    const originalScore = evaluation.score;
                    evaluation.score = (originalScore * 0.6) + (fidelityScore * 0.4);

                    // Store Raw Scores for Optimization Logic
                    evaluation.scoringRaw = originalScore;
                    evaluation.fidelityRaw = fidelityScore;

                    if (fidelityScore < 1.0) {
                        evaluation.feedback += ` [Fidelity Issues: ${[...new Set(criticalIssues)].join(', ')}]`;
                    }
                }

                lastEvaluation = evaluation; // Persist for next iteration mutation

                // --- CONFIDENCE MODULATION ---
                let effectiveThreshold = targetThreshold;
                if (evaluation.confidence < 0.5) {
                     effectiveThreshold = Math.max(0.5, targetThreshold - 0.15);
                     console.warn(`[Optimization] Low Confidence (${evaluation.confidence}). Lowering threshold to ${effectiveThreshold}`);
                }

                // Safety Brake: If confidence is VERY low and we've tried a few times, abort.
                if (evaluation.confidence < 0.3 && attempts > 3) {
                    console.warn(`[Optimization] Confidence Critical (${evaluation.confidence}). Aborting Optimization.`);
                    attempts = maxAttempts; // Force exit
                }

                // --- COHESION VETO ---
                // If specific sub-metrics are critical failures, cap the score to force retry.
                if (evaluation.details) {
                     const { motivicRepetition, rhythmicLock } = evaluation.details;

                     // 1. Motivic Veto (Must have some repetition)
                     // Only apply if archetype demands it (ScoringEngine handles weight, but here we handle "Broken")
                     if (motivicRepetition !== undefined && motivicRepetition < 0.4) {
                         evaluation.score = Math.min(evaluation.score, 0.65); // Cap below target (0.7)
                         evaluation.feedback += " [Veto: Weak Motifs]";
                     }

                     // 2. Rhythmic Lock Veto (Must be tight)
                     // Important for Action/Epic
                     if (rhythmicLock !== undefined && rhythmicLock < 0.4) {
                         evaluation.score = Math.min(evaluation.score, 0.6);
                         evaluation.feedback += " [Veto: Loose Rhythm]";
                     }
                }

                console.log(`[Optimization] Attempt ${attempts+1}: Score ${evaluation.score.toFixed(2)} (Confidence ${evaluation.confidence}) - ${evaluation.feedback}`);

                // Telemetry: Calculate Raw Stats
                const noteCount = allEvents.length;
                const uniquePitches = new Set(allEvents.filter(e => typeof e.d === 'number').map(e => e.d % 12)).size;
                const totalVel = allEvents.reduce((acc, e) => acc + (e.vel || 0), 0);
                const avgVelocity = noteCount > 0 ? (totalVel / noteCount).toFixed(2) : 0;

                // Observability: Log to audit trail
                auditLog.push({
                    iteration: attempts + 1,
                    score: evaluation.score,
                    confidence: evaluation.confidence,
                    delta: attempts > 0 ? (evaluation.score - (auditLog[attempts-1]?.score || 0)).toFixed(2) : 0,
                    feedback: evaluation.feedback,
                    breakdown: evaluation.details, // Sub-Metric Breakdown
                    telemetry: {
                        noteCount,
                        uniquePitches,
                        avgVelocity
                    }
                });

                const currentResult = {
                    progression: chordProgression,
                    events: allEvents,
                    tempoMap: tempoMap,
                    instruments: context.instruments,
                    auditLog: auditLog, // Return log
                    metadata: {
                        engineVersion: this.engine.version,
                        profile: profile,
                        effectiveScale: context.scaleType,
                        score: evaluation.score,
                        scoringRaw: evaluation.scoringRaw,
                        fidelityRaw: evaluation.fidelityRaw,
                        confidence: evaluation.confidence,
                        feedback: evaluation.feedback
                    }
                };

                // Store best result so far
                if (evaluation.score > bestScore) {
                    bestScore = evaluation.score;
                    bestResult = currentResult;
                    // Persist Best Config for Hill Climbing
                    if (context.config) {
                        bestConfig = JSON.parse(JSON.stringify(context.config));
                    }
                }

                lastScore = evaluation.score;

                // Explicit Exit if Effective Threshold Met
                if (lastScore >= effectiveThreshold) {
                    console.log(`[Optimization] Threshold met (Score ${lastScore} >= ${effectiveThreshold}). Finishing.`);
                    break;
                }

            } else {
                 // No events generated? Serious failure.
                 console.warn("[Optimization] No events generated. Retrying blindly.");
                 lastScore = 0.0;

                 // Defect Fix: Ensure we return the progression even if orchestration fails
                 if (chordProgression.length > 0 && bestScore === -1) {
                      bestResult = {
                          progression: chordProgression,
                          events: [],
                          tempoMap: tempoMap,
                          instruments: context.instruments,
                          auditLog: auditLog,
                          metadata: {
                              engineVersion: this.engine.version,
                              profile: profile,
                              effectiveScale: context.scaleType,
                              score: 0,
                              feedback: "Orchestration Failed"
                          }
                      };
                      bestScore = 0;
                 }
            }

            attempts++;
        }

        // If loop was skipped (unknown genre), ensure we return a result (likely attempt 0 failed or didn't run loop logic if we structure incorrectly)
        // Actually, if shouldOptimize is false, loop doesn't run. We need to run at least ONCE.
        // Logic Fix: Use a do-while or run once before loop?
        // Or simply: if (!shouldOptimize) runOnce() else runLoop().
        // For simplicity, let's just execute standard flow if not optimized.

        if (!shouldOptimize && !bestResult) {
            console.log('[GenerationPipeline] No optimization profile found. Running standard pass.');
            // Standard Single Pass (Code duplication avoidance: we could wrap the generation logic in a method)
            // But for now, since we are inside `execute`, we can't easily jump back.
            // Wait, the loop condition was `while (shouldOptimize ...)`. If false, it skips.
            // So we need a fallback execution here.

            return this.executeStandardPass(profile, context);
        }

        console.log(`[GenerationPipeline] Pipeline Complete. Returning best result (Score: ${bestScore}).`);
        return bestResult || {
            progression: [],
            events: [],
            tempoMap: [],
            instruments: context.instruments,
            auditLog: auditLog,
            metadata: { engineVersion: this.engine.version }
        };
    }

    executeStandardPass(profile, context) {
        // Fallback for non-optimized genres
        // Re-implement the linear flow (copy-paste of the inner loop logic essentially, minus mutation)
        // For cleaner code, we should refactor the inner generation steps into `generatePass(profile, context)`.

        // 2. Harmony Step
        let chordProgression = [];
        try { chordProgression = this.stepHarmony(profile, context); } catch(e) { console.error(e); }

        if (chordProgression.length === 0) {
             // Safety Pedal Point Logic (Duplicated)
             const rootKey = context.rootKey || 'C';
             const rootIdx = NOTES.indexOf(normalizeKey(rootKey));
             const scaleType = context.scaleType || 'major';
             let barOffset = 0;
             context.structure.forEach(sect => {
                 const bars = sect.bars || 4;
                 const chord = { degree: 0, rootPitchClass: rootIdx, quality: scaleType === 'minor'?'minor':'major', type: 'triad', notes: [rootIdx, (rootIdx+4)%12, (rootIdx+7)%12], barIndex: barOffset, duration: 4, section: sect.type, tension: 0.1 };
                 for(let b=0; b<bars; b++) chordProgression.push({...chord, barIndex: barOffset+b});
                 barOffset += bars;
             });
        }

        let voicedChords = [], allEvents = [], tempoMap = [];
        if (chordProgression.length > 0) {
            try { voicedChords = this.stepVoicing(chordProgression, profile, context); this.validateAndRepairChords(voicedChords, context); } catch(e) { console.error(e); }
            try { if(voicedChords.length>0) allEvents = this.stepOrchestration(voicedChords, profile, context); } catch(e) { console.error(e); }
        }

        try { allEvents = this.stepPostProcessing(allEvents, voicedChords, context, profile); } catch(e) {}
        try { allEvents = applyExpressionCurves(allEvents, context); } catch(e) {}
        try {
            tempoMap = generateTempoMap(context.structure, context.bpm||120, { humanize: profile.humanizeTempo, rubatoIntensity: context.traits?.rubatoIntensity||0, random: this.random, getTension: context.getTension });
        } catch(e) {}

        return {
            progression: chordProgression,
            events: allEvents,
            tempoMap: tempoMap,
            instruments: context.instruments,
            metadata: { engineVersion: this.engine.version, profile: profile, effectiveScale: context.scaleType }
        };
    }

    /**
     * Applies Simulated Annealing / Temperature Decay mutation to the context.
     * @param {Object} evaluation - Full evaluation result (includes gradients).
     */
    mutateContext(context, score, attempt, profile, evaluation = null) {
        console.log(`[Mutation] Applying mutation for Score ${score} (Attempt ${attempt})`);

        // High Temperature: Aggressive Mutation (Score < 0.4)
        if (score < 0.4) {
             console.log('[Mutation] High Temp: Rerolling Seed and resetting Config.');
             // Reroll Seed
             const newSeed = (context.random ? Math.floor(context.random.next() * 1000000) : Date.now());
             context.random.setSeed(newSeed); // Explicit re-seed

             // Update profile seed to ensure consistency if checked elsewhere
             profile.seed = newSeed; // Update profile reference for downstream usage

             // Re-initialize Config completely
             context.config = getSeededConfig(profile.harmonySource, newSeed, profile);

             // Re-initialize Tension Curve
             context.getTension = generateTensionCurve(context.structure, profile.harmonySource, newSeed);

             // FIX: Unlock Ensemble Mutation
             // Clear instruments to force Orchestrator to re-select a potentially better ensemble
             if (!context.lockInstruments) {
                 context.instruments = [];
                 context.barInstrumentation = null;
             }

             // State Synchronization: Propagate to Engine
             // Fix: "Stale Worker" - Ensure MotifManager and other persistent modules are reset
             this.engine.updateContext(context);
        }
        // Medium Temperature: Gradient Descent / Vector Tweak (Score 0.4 - 0.9)
        else if (score < 0.9) {
            console.log('[Mutation] Med Temp: Tweaking Vectors via Gradient Descent.');

            const scoringRaw = (evaluation && evaluation.scoringRaw !== undefined) ? evaluation.scoringRaw : 1.0;
            const fidelityRaw = (evaluation && evaluation.fidelityRaw !== undefined) ? evaluation.fidelityRaw : 1.0;

            // --- TWO-FACTOR OPTIMIZATION LOGIC ---

            // Scenario A: Fidelity Failure (Qualitative) -> Adjust Genre Scalars
            // If Fidelity is significantly lower than Scoring, we have a "Vibe" problem.
            if (fidelityRaw < scoringRaw - 0.1) {
                 console.log(`[Mutation] Scenario A: Fidelity Lag (${fidelityRaw.toFixed(2)} vs ${scoringRaw.toFixed(2)}). Tuning Genre Scalars.`);

                 // 1. Boost Timbral Motion (Dynamic Variance)
                 if (context.config.vectors && context.config.vectors.timbralMotion !== undefined) {
                     context.config.vectors.timbralMotion = Math.min(1.0, context.config.vectors.timbralMotion + 0.15);
                 }

                 // 2. Boost Accents (Dynamics)
                 if (context.config.velocities) {
                     context.config.velocities.accent = Math.min(0.4, context.config.velocities.accent + 0.05);
                 }

                 // 3. Humanize (Timing/Feel)
                 // Note: Profile mutation might not persist to next loop's deep config unless re-seeded,
                 // but we can try to adjust current context or profile for re-roll.
                 if (profile.humanize) profile.humanize = Math.min(100, profile.humanize + 10);
            }
            // Scenario B: Scoring Failure (Quantitative) -> Adjust Theory Scalars
            else if (context.config && context.config.vectors && evaluation && evaluation.gradients) {
                console.log(`[Mutation] Scenario B: Scoring Optimization. Tuning Theory Scalars.`);

                const gradients = evaluation.gradients;
                const nudge = 0.1; // Standard step size

                // 1. Harmonic Rhythm Gradient -> Harmonic Complexity
                // Positive Gradient (Needs Slower) -> Lower Complexity
                // Negative Gradient (Needs Faster) -> Higher Complexity
                if (gradients.harmonicRhythm !== undefined && Math.abs(gradients.harmonicRhythm) > 0.2) {
                    const dir = gradients.harmonicRhythm > 0 ? -1 : 1;
                    context.config.vectors.harmonicComplexity = Math.max(0.1, Math.min(1.0, context.config.vectors.harmonicComplexity + (nudge * dir)));
                    console.log(`[Mutation] Harmonic Rhythm Gradient ${gradients.harmonicRhythm.toFixed(2)} -> Adj Complexity`);
                }

                // 2. Interval Entropy Gradient -> Melodic Range
                // Positive Gradient (Needs More Steps) -> Reduce Range (Closer intervals)
                // Negative Gradient (Needs More Leaps) -> Increase Range
                if (gradients.intervalEntropy !== undefined && Math.abs(gradients.intervalEntropy) > 0.1) {
                    const dir = gradients.intervalEntropy > 0 ? -1 : 1;
                    if (context.config.vectors.melodicRange !== undefined) {
                        context.config.vectors.melodicRange = Math.max(0.1, Math.min(1.0, context.config.vectors.melodicRange + (nudge * dir)));
                        console.log(`[Mutation] Entropy Gradient ${gradients.intervalEntropy.toFixed(2)} -> Adj Range`);
                    }
                }

                // 3. Dynamic Range Gradient -> Timbral Motion (Backup for Scoring)
                if (gradients.dynamicRange !== undefined && Math.abs(gradients.dynamicRange) > 0.1) {
                    const dir = gradients.dynamicRange > 0 ? 1 : -1;
                    if (context.config.vectors.timbralMotion !== undefined) {
                        context.config.vectors.timbralMotion = Math.max(0.1, Math.min(1.0, context.config.vectors.timbralMotion + (nudge * dir)));
                        console.log(`[Mutation] Dynamic Gradient ${gradients.dynamicRange.toFixed(2)} -> Adj Motion`);
                    }
                }

                // 4. Voice Distribution Gradient -> Melodic Density / Range
                if (gradients.voiceDistribution) {
                    const { low, high } = gradients.voiceDistribution;
                    // If Highs are lacking (positive delta), increase Range or Density
                    if (high > 0.1) {
                        context.config.vectors.melodicRange = Math.min(1.0, (context.config.vectors.melodicRange || 0.5) + 0.1);
                        console.log(`[Mutation] High Voice Deficit -> Boosting Range`);
                    }
                    // If Lows are lacking, boost energy
                    if (low > 0.1) {
                        context.config.vectors.energy = Math.min(1.0, (context.config.vectors.energy || 0.5) + 0.1);
                        console.log(`[Mutation] Low Voice Deficit -> Boosting Energy`);
                    }
                }
            } else {
                // Fallback to Random Tweak if no gradients (Legacy logic)
                const random = context.random || new Random();
                const nudge = 0.1;
                const oldHarm = context.config.vectors.harmonicComplexity;
                const dir = (random.next() > 0.5 ? 1 : -1);
                context.config.vectors.harmonicComplexity = Math.max(0.1, Math.min(1.0, oldHarm + (nudge * dir)));
                console.log('[Mutation] Fallback Random Tweak applied.');
            }

            // Sync Profile (Common)
            if (context.config.vectors) {
                profile.rhythmicDensity = context.config.vectors.melodicDensity;
            }
            // Re-roll Traits
            if (context.ensembleKey) {
                context.traits = this.engine.getGenreTraits(context.ensembleKey, profile.harmonySource);
            }
        }
        // Low Temperature: Micro Adjustments (Score > 0.7)
        else {
            console.log('[Mutation] Low Temp: Micro-Adjustments.');
            if (context.config && context.config.velocities) {
                // Boost dynamics
                context.config.velocities.base = Math.min(0.9, context.config.velocities.base + 0.05);
                context.config.velocities.accent = Math.min(0.3, context.config.velocities.accent + 0.02);
            }
        }
    }

    stepInitialization(profile, context) {
        const ensembleKey = context.ensembleKey || 'orchestra';
        const harmonyTraits = this.engine.getGenreTraits(ensembleKey, profile.harmonySource);

        // Detect Genre Change
        if (this.lastHarmonySource !== profile.harmonySource) {
            context.forceNewSong = true;
            this.lastHarmonySource = profile.harmonySource;
        }

        context.traits = harmonyTraits;

        // Refactored Scale Selection (Influence Engine)
        // Replaces strict override `getEffectiveScale` with Weighted Resolution
        const resolver = new ParameterResolver(context.random);
        const scaleCandidates = [];

        // 1. User Global Request (Ultimate Authority)
        if (profile.userScale && profile.userScale !== 'Auto') {
            scaleCandidates.push({ value: profile.userScale, weight: INFLUENCE_WEIGHTS.GLOBAL, force: true });
        }

        // 2. Genre Defaults
        const genreScale = this.engine.getEffectiveScale('Auto', harmonyTraits); // Get default
        scaleCandidates.push({ value: genreScale, weight: INFLUENCE_WEIGHTS.LOCAL });

        // 3. Narrative Influence (e.g. Tension -> Minor/Locrian?)
        // (Could add logic here later)

        const scaleType = resolver.resolveDiscrete(scaleCandidates) || 'major';

        // Fix: Ensure Root Key is defined
        if (!context.rootKey) {
            context.rootKey = 'C';
        }

        if (harmonyTraits && harmonyTraits.defaults && harmonyTraits.defaults.timeSig) {
             if (!context.lockTimeSig) {
                 context.timeSig = harmonyTraits.defaults.timeSig;
             }
        }

        if (context.structure) {
            context.structure.forEach(s => {
                if (s.timeSignature) {
                    s.timeSig = s.timeSignature.numerator / (s.timeSignature.denominator / 4);
                }
            });
        }

        context.scaleType = scaleType;

        // SeededConfig generation is now handled in MusicEngine before calling pipeline, or we do it here.
        // Doing it here ensures context.config is set.
        context.config = getSeededConfig(profile.harmonySource, profile.seed, profile);
        context.config.harmonySource = profile.harmonySource; // Inject for Orchestrator/RoleAllocator logic

        // Task 2: The "Global Vault" (Song Memory)
        // Initialize memory structure for persistence across sections
        if (!context.songMemory) {
            context.songMemory = { motifs: {}, themes: [] };
        }

        // Inject BPM for Tempo-Aware strategies
        context.bpm = context.bpm || 120; // Default if missing

        // Task 2: Motif Persistence (The "Memory" Fix)
        // Only recreate MotifManager if it doesn't exist OR if we forced a full reset.
        // We reuse the instance to preserve thematic material across generations.
        if (!this.engine.motifManager || context.forceNewSong) {
             this.engine.motifManager = new MotifManager({
                rootKey: context.rootKey,
                scaleType: context.scaleType,
                timeSig: context.timeSig,
                random: this.random,
                config: context.config
            });
        } else {
            // Update context of existing manager
            this.engine.motifManager.rootKey = context.rootKey;
            this.engine.motifManager.scaleType = context.scaleType;
            this.engine.motifManager.timeSig = context.timeSig;
            this.engine.motifManager.config = context.config;
            this.engine.motifManager.random = this.random; // Update PRNG
        }

        context.motifManager = this.engine.motifManager;

        // Task 1: "Structural Integrity" - Respect Genre Defaults if Structure is Missing
        if (!context.structure || context.structure.length === 0) {
            // Check if genre traits provide a default structure
            if (harmonyTraits && harmonyTraits.defaults && harmonyTraits.defaults.structure) {
                // Clone to prevent mutation of the registry
                context.structure = JSON.parse(JSON.stringify(harmonyTraits.defaults.structure));
                console.log(`[GenerationPipeline] Loaded default structure for ${profile.harmonySource}`);
            } else {
                context.structure = [{ type: 'intro', bars: 4 }];
            }
        }

        const allowModulation = (profile.userScale === 'Auto');
        const suggested = suggestSectionKeys(context.structure, context.rootKey, context.scaleType, allowModulation);
        if (Array.isArray(suggested)) {
            context.structure = suggested;
        } else {
            console.warn('[GenerationPipeline] suggestSectionKeys returned invalid structure, keeping original.');
        }

        // Narrative Tension
        // Pass genre to enforce ceilings, and seed for dynamic ranges (Task 2)
        // Fix: generateTensionCurve returns a Function, not an Array. Directly assign it.
        context.getTension = generateTensionCurve(context.structure, profile.harmonySource || 'default', profile.seed);

        if (!allowModulation) {
            context.structure.forEach(s => {
                s.key = context.rootKey;
                s.scale = context.scaleType;
            });
        }
    }

    stepHarmony(profile, context) {
        const harmonyTraits = context.traits;

        // --- Fix #1: The "Invisible" Harmony Engine ---
        // Explicitly instantiate HarmonyEngine (Manager) as a primary system component.
        // It consumes Context and Traits, and produces the Chord Progression.
        // Fix: "Stale Worker" - Ensure we use the CURRENT context.random, not a cached one
        context.harmonyEngine = new HarmonyManager(context.random);

        // 1. Generate Base Progression via Functional Graph (The Brain)
        // Replacing Legacy Template System (MusicEngine.getChordProgression)
        let chordProgression = context.harmonyEngine.generateGraphProgression(context.structure, context);

        // Phase 6: Target-Based Progression Enhancement (The "Brain" logic)
        try {
            chordProgression = context.harmonyEngine.generateTargetedProgression(chordProgression, context);

            // Store Harmony Meta-State in Context for downstream consumers (Melody/Rhythm)
            // This ensures MelodyEngine isn't just seeing raw chords, but knows *why* (e.g. cadence type)
            let harmonicRhythm = { avgDuration: 4, pace: 'moderate' };
            if (typeof context.harmonyEngine.analyzeHarmonicRhythm === 'function') {
                harmonicRhythm = context.harmonyEngine.analyzeHarmonicRhythm(chordProgression);
            }

            context.harmonyState = {
                progressionLength: chordProgression.length,
                hasModulation: chordProgression.some(c => c.function && c.function.includes('pivot')),
                harmonicRhythm: harmonicRhythm
            };
        } catch (e) {
            console.warn('[GenerationPipeline] HarmonyManager enhancement failed, using base progression.', e);
        }

        console.log(`[GenerationPipeline] Generated ${chordProgression.length} chords.`);
        return chordProgression;
    }

    stepVoicing(chordProgression, profile, context) {
        const ensembleKey = context.ensembleKey || 'orchestra';
        const harmonyTraits = context.traits;
        const voicingTraits = this.engine.getGenreTraits(ensembleKey, profile.voicingSource);

        const voiceLeadingStrictness = (harmonyTraits && (harmonyTraits.voiceLeadingStrictness || (harmonyTraits.defaults && harmonyTraits.defaults.voiceLeadingStrictness))) || 'cinematic';
        const doublingStrategy = (voicingTraits && (voicingTraits.doublingStrategy || (voicingTraits.defaults && voicingTraits.defaults.doublingStrategy))) || 'classic';

        const pedalProcessed = applyPedalPoint(chordProgression, context.rootKey);
        const scale = this.engine.getScale(context.rootKey, context.scaleType);

        const tensionChords = pedalProcessed.map(chord => {
            const tension = calculateTension(chord, scale, 1.0, context); // Pass context for complexity
            return { ...chord, tension };
        });

        let voicedChords = this.engine.applyVoicing(tensionChords, voicingTraits, context, doublingStrategy);

        const isPower = voicingTraits && (voicingTraits.voicing.toLowerCase().includes('rock') || voicingTraits.voicing.toLowerCase().includes('power'));
        if (!isPower) {
            voicedChords = applyVoiceLeading(voicedChords, voiceLeadingStrictness, harmonyTraits);

            const useSATB = (ensembleKey === 'orchestra' || ensembleKey === 'string_quartet') ||
                            (harmonyTraits && harmonyTraits.satb === true) ||
                            (voicingTraits && voicingTraits.satb === true);

            if (useSATB) {
                const satbRanges = voicingTraits ? voicingTraits.satbRanges : null;
                voicedChords = applySATBVoiceLeading(voicedChords, voiceLeadingStrictness, satbRanges);
            }
        }
        return voicedChords;
    }

    // Task 3: The "Anti-Mud" Governor (Vertical Slotting)
    // Refactored to external module 'VerticalSlotting.js' in Operation Soft Slotter
    stepVerticalSlotting(abstractTracks, context, allocationMap = {}) {
        return applyVerticalSlotting(abstractTracks, context, allocationMap);
    }

    // Round 3 - Phase 3: Negative Space (Global Silence)
    stepNegativeSpace(abstractTracks, context) {
        // Determine Break Locations (e.g. End of 4-bar phrase in High Intensity)
        // Check intensity curve
        const curve = context.intensityCurve || [];
        const breakBars = [];

        curve.forEach((intensity, barIdx) => {
            // Potential break if high intensity and end of 4-bar phrase
            if (intensity > 0.7 && (barIdx + 1) % 8 === 0) { // Reduced freq: Every 8 bars, intensity > 0.7
                // Dynamic Break Probability: Higher Intensity -> Higher chance of dramatic break
                // 0.6 -> 0.1, 1.0 -> 0.3 (Reduced max chance)
                const breakChance = 0.1 + ((intensity - 0.7) / 0.3) * 0.2;
                if (this.random.next() < breakChance) {
                    breakBars.push({ bar: barIdx, type: 'stop_time' });
                }
            }
        });

        // --- NEW: Structural Stop Time (Drop Transitions) ---
        if (context.structure) {
            let currentBar = 0;
            context.structure.forEach((section, idx) => {
                const nextSection = context.structure[idx+1];
                const sectionBars = section.bars || 4;

                // Task 4: Dramatic Stop (Contextual Ensemble Breath)
                // 1. Pre-Drop Stop Time (Last bar of Build/Rise)
                if (section.type === 'BUILD' || section.type === 'RISE') {
                    if (nextSection && (nextSection.type === 'DROP' || nextSection.type === 'CHORUS')) {
                        // Check Transition Energy (approx via intensity curve or just assume High)
                        // Fix: barOffset indexing for variable section lengths
                        const tensionBarIndex = currentBar + sectionBars - 1;
                        const tension = (context.intensityCurve && context.intensityCurve[tensionBarIndex]) !== undefined
                                        ? context.intensityCurve[tensionBarIndex] : 0.9;

                        // If Tension > 0.8, VETO generation for melodic/harmonic instruments in the last 2 beats
                        // This is handled by 'stop_time' type.
                        // Force it if tension is high.
                        if (tension > 0.8) {
                            const lastBarOfBuild = currentBar + sectionBars - 1;
                            if (!breakBars.some(b => b.bar === lastBarOfBuild)) {
                                // "Stop Time" usually means silence on 2,3,4 or just 4.
                                // Let's make it strict: Silence beats 3 & 4.
                                breakBars.push({ bar: lastBarOfBuild, type: 'dramatic_stop' });
                            }
                        }
                    }
                }

                // 2. Drop "Fake Out" (Silence on Beat 1 of Drop)
                if (section.type === 'DROP') {
                     if (this.random.next() > 0.8) { // Reduced chance
                         const dropBar = currentBar; // First bar of Drop
                         breakBars.push({ bar: dropBar, type: 'mute_one' });
                     }
                }

                currentBar += sectionBars;
            });
        }

        // Apply Mutes
        breakBars.forEach(brk => {
            const barStart = brk.bar * 4;
            let muteStart, muteEnd;

            // --- FIX: "One Beat Limit" ---
            // User Directives: "One beat at most" for any silence. No whole bar silence.
            // All stop times are reduced to single beat mutes.

            if (brk.type === 'stop_time') {
                // Was: Silence 2, 3, 4.
                // Now: Silence Beat 1 only (Drop Fakeout) OR Beat 4 (breath).
                // Let's make 'stop_time' a Breath on Beat 4.
                muteStart = barStart + 3.0; // Beat 4 start
                muteEnd = barStart + 4.0;   // Beat 4 end
            } else if (brk.type === 'dramatic_stop') {
                // Was: Beats 3 and 4.
                // Now: Silence only Beat 4 (Transition Snap)
                muteStart = barStart + 3.0;
                muteEnd = barStart + 4.0;
            } else if (brk.type === 'mute_one') {
                // Silence Beat 1, Play 2, 3, 4
                // This is 1 beat. Acceptable.
                muteStart = barStart;
                muteEnd = barStart + 1.0;
            }

            Object.keys(abstractTracks).forEach(role => {
                // Feature: Solo Carry-Through
                // Don't mute Melody/Lead during stop time to allow for fills/solos
                if (role === ABSTRACT_ROLES.MELODY || role === ABSTRACT_ROLES.COUNTER_MELODY) return;

                if (abstractTracks[role]) {
                    abstractTracks[role] = abstractTracks[role].filter(ev => {
                        return ev.s < muteStart || ev.s >= muteEnd;
                    });
                }
            });
            console.log(`[GenerationPipeline] Applied ${brk.type} at Bar ${brk.bar}`);
        });

        return abstractTracks;
    }

    // Phase 6: Activity Masking (Rhythmic Counterpoint)
    stepActivityMasking(abstractTracks, context) {
        // Calculate Melody Density map per bar
        const melodyEvents = abstractTracks[ABSTRACT_ROLES.MELODY] || [];
        const barDensity = {}; // { barIndex: density (0-1) }

        // Group by bar
        melodyEvents.forEach(ev => {
            const bar = Math.floor(ev.s / 4); // Assuming 4/4 mostly, simplistic
            if (!barDensity[bar]) barDensity[bar] = 0;
            barDensity[bar] += (1 / 16); // Each event adds some density weight
        });

        // Base thresholds
        const baseThresholds = (context.config && context.config.orchestration && context.config.orchestration.activityMasking) || {
            highThreshold: 0.75,
            lowThreshold: 0.15
        };

        // Apply Masks
        Object.keys(barDensity).forEach(barStr => {
            const bar = parseInt(barStr);
            const density = Math.min(1.0, barDensity[bar]);

            // Dynamic Thresholds based on Intensity
            const intensity = (context.intensityCurve && context.intensityCurve[bar]) || 0.5;
            const dynamicHigh = Math.min(0.95, baseThresholds.highThreshold + (intensity > 0.7 ? 0.15 : 0));

            // Task B: The "Conversational" Arranger (Trading Fours)
            // If Melody (Lead A) density > 0.6, Counter-Melody (Lead B) must Rest or Sustain.
            if (density > 0.6) {
                // Force Counter-Melody to chill
                const cmEvents = abstractTracks[ABSTRACT_ROLES.COUNTER_MELODY];
                if (cmEvents) {
                    abstractTracks[ABSTRACT_ROLES.COUNTER_MELODY] = cmEvents.filter(ev => {
                        const evBar = Math.floor(ev.s / 4);
                        if (evBar === bar) {
                            // Filter strategy: Keep only long notes (Sustain) or rests
                            // If note is short (< 1.0), remove it (Rest).
                            // If note is long (>= 1.0), keep it (Sustain).
                            return (ev.len >= 1.0);
                        }
                        return true;
                    });
                }
            }

            // Fix: "Stripey Climax" - Disable activity masking if intensity is very high (Wall of Sound)
            // Also bypass for specific section types explicitly
            const isWallOfSound = (context.section === 'CLIMAX' || context.section === 'DROP' || context.section === 'SOLO');

            if (intensity > 0.85 || isWallOfSound) {
                // Do not mask. Allow full density.
            } else if (density > dynamicHigh) {
                // Too busy. Thin out Harmony/CounterMelody in this bar.
                [ABSTRACT_ROLES.HARMONY, ABSTRACT_ROLES.COUNTER_MELODY].forEach(role => {
                    if (abstractTracks[role]) {
                        abstractTracks[role] = abstractTracks[role].filter(ev => {
                            const evBar = Math.floor(ev.s / 4);
                            if (evBar === bar) {
                                // Keep only strong beats (0, 2)
                                const beat = ev.s % 4;
                                return (beat % 2 === 0);
                            }
                            return true;
                        });
                    }
                });
            }
        });

        return abstractTracks;
    }

    stepOrchestration(voicedChords, profile, context) {
        const ensembleKey = context.ensembleKey || 'orchestra';
        const harmonyTraits = context.traits;
        const rhythmTraits = this.engine.getGenreTraits(ensembleKey, profile.rhythmSource);

        // Inject phrase structure
        if (context.config && context.config.orchestration && context.config.orchestration.phraseStructure) {
            context.phraseStructure = context.config.orchestration.phraseStructure;
        }

        if (!context.instruments) context.instruments = [];

        // Determine Dynamic Instrumentation (Per-Bar)
        try {
             // Operation Curated Ensembles: Ensure we have the right instruments
             if (!context.instruments || context.instruments.length === 0) {
                 console.log('[GenerationPipeline] No instruments provided. Selecting Curated Ensemble.');
                 context.instruments = selectEnsembleForGenre(profile.harmonySource, profile.seed);
             }

             context.barInstrumentation = determineDynamicInstrumentation(
                 context.structure,
                 context.instruments,
                 context.config,
                 profile.seed,
                 context.intensityCurve // Passing the curve calculated in execute()
             );
             console.log('[GenerationPipeline] Determined dynamic instrumentation.');
        } catch (e) {
             console.warn('[GenerationPipeline] Orchestrator activation failed', e);
        }

        // Frequency Spectrum Management
        context.spectrumRegistry = { low: false, mid: false, high: false };

        // Orchestral Dialogue Analysis
        context.dialogueManager = new DialogueManager();

        let allEvents = [];
        context.hasWoodwinds = context.instruments.some(i => i.id.includes('flute') || i.id.includes('oboe') || i.id.includes('clarinet'));

        // Group Chords by Section
        const chordsBySection = new Array(context.structure.length).fill(0).map(() => []);
        voicedChords.forEach(chord => {
            // Map barIndex to section
            let t = 0;
            for(let i=0; i<context.structure.length; i++) {
                const bars = context.structure[i].bars || 4;
                if (chord.barIndex >= t && chord.barIndex < t + bars) {
                    chordsBySection[i].push(chord);
                    break;
                }
                t += bars;
            }
        });

        // Generation State for Continuity
        const genState = {
            lastMelodyNote: null,
            lastCounterNote: null,
            lastHarmonyNote: null,
            // Fix: Persist Entropy State across sections for Panic Mode
            entropyState: { steps: 0, intervals: 0, stepRatio: 1.0 }
        };

        // --- SECTION LOOP ---
        let barOffset = 0; // Fix: Track absolute bar index for variable length sections
        const WALL_OF_SOUND_INTENSITY_THRESHOLD = 0.85;

        for(let i=0; i<context.structure.length; i++) {
            const section = context.structure[i];
            const sectionChords = chordsBySection[i];
            if (!sectionChords || sectionChords.length === 0) {
                barOffset += (section.bars || 4);
                continue;
            }

            console.log(`[GenerationPipeline] Orchestrating Section ${i}: ${section.type}`);

            // Task 2: The "Sectional Arc" (Narrative Scaling)
            // Fix: "Ghost Town" - Boosted scalars for Intro/Outro/Build to prevent density collapse.
            let sectionScalar = 1.0;
            const type = section.type.toLowerCase();
            if (type.includes('intro')) sectionScalar = 0.75; // Was 0.5
            else if (type.includes('build') || type.includes('rise')) sectionScalar = 1.0; // Was 0.9
            else if (type.includes('chorus') || type.includes('drop') || type.includes('climax')) sectionScalar = 1.1;
            else if (type.includes('outro')) sectionScalar = 0.7; // Was 0.4

            // Apply Section Scalar to a Local Config Clone
            const sectionConfig = JSON.parse(JSON.stringify(context.config)); // Deep clone
            if (sectionConfig.vectors) {
                // Scale relevant complexity vectors
                sectionConfig.vectors.melodicDensity = Math.max(0.1, Math.min(1.0, sectionConfig.vectors.melodicDensity * sectionScalar));
                sectionConfig.vectors.harmonicComplexity = Math.max(0.1, Math.min(1.0, sectionConfig.vectors.harmonicComplexity * sectionScalar));
                sectionConfig.vectors.rhythmicDensity = Math.max(0.1, Math.min(1.0, (sectionConfig.vectors.rhythmicDensity || sectionConfig.vectors.melodicDensity) * sectionScalar));
                // Intensity (Energy) might also scale?
                sectionConfig.vectors.energy = Math.max(0.1, Math.min(1.0, sectionConfig.vectors.energy * sectionScalar));
            }

            // Create Local Context
            const sectionContext = {
                ...context,
                config: sectionConfig,
                section: section.type, // Ensure exact section type is passed
                sectionLength: section.bars || 4, // Task 1: Canvas Check (Duration Awareness)
                voiceRegistry: createRegistry() // Task 4: Safety Seal (Data Isolation)
            };

            // 1. Dynamic Role Allocation (Task 2: Section-Based Bias)
            // Use harmonyTraits for Role Criteria/Behaviors, as it defines the Orchestral palette.
            const allocationMap = allocateRoles(context.instruments, harmonyTraits, section.type);

            // Special Ensembles Handling
            this.applySpecialEnsembles(harmonyTraits, sectionContext, allocationMap);

            // 2. Generate Abstract Tracks
            let abstractTracks = this.engine.generateAbstractTracks(
                sectionChords,
                rhythmTraits,
                sectionContext, // Pass Modified Context
                profile,
                allocationMap,
                genState
            );

            // Update State
            if (abstractTracks[ABSTRACT_ROLES.MELODY] && abstractTracks[ABSTRACT_ROLES.MELODY].length > 0) {
                const evs = abstractTracks[ABSTRACT_ROLES.MELODY];
                genState.lastMelodyNote = evs[evs.length - 1].d;
            }
            if (abstractTracks[ABSTRACT_ROLES.COUNTER_MELODY].length > 0) {
                const evs = abstractTracks[ABSTRACT_ROLES.COUNTER_MELODY];
                genState.lastCounterNote = evs[evs.length - 1].d;
            }
            if (abstractTracks[ABSTRACT_ROLES.HARMONY_RHYTHMIC].length > 0) {
                const evs = abstractTracks[ABSTRACT_ROLES.HARMONY_RHYTHMIC];
                genState.lastHarmonyNote = evs[evs.length - 1].d;
            }

            // Kick Pattern Extraction
            try {
                const kickEvents = abstractTracks[ABSTRACT_ROLES.PERC_LOW] || [];
                if (kickEvents.length > 0) {
                    context.kickPatternEvents = kickEvents.sort((a,b) => a.s - b.s);
                }
            } catch (e) {}

            // 3. Post-Processing
            try {
                // Pass sectionContext to ensure isWallOfSound check works
                abstractTracks = this.stepActivityMasking(abstractTracks, sectionContext);

                // Task 4: Breathing Enforcer (Smart Crowd Control)
                abstractTracks = this.stepBreathingEnforcer(abstractTracks, sectionContext);

                // Task 3: Vertical Slotting (Anti-Mud Governor) + Feedback Loop
                // Fix: "Stripey Climax" - Disable vertical slotting if intensity is very high (Wall of Sound)
                // Also bypass for specific high-energy section types to ensure Wall of Sound.
                const currentIntensity = (context.intensityCurve && context.intensityCurve[barOffset]) || 0.5;
                const isWallOfSound = (section.type === 'CLIMAX' || section.type === 'DROP' || section.type === 'SOLO');

                if (currentIntensity <= WALL_OF_SOUND_INTENSITY_THRESHOLD && !isWallOfSound) {
                    const result = this.stepVerticalSlotting(abstractTracks, context, allocationMap);

                    // Unpack result (it might return just tracks if legacy, or {tracks, stats})
                    if (result.stats) {
                        abstractTracks = result.tracks;
                        // --- Fix #3: The "Deaf Composer" (Feedback Loop) ---
                        // If we are fighting the mix too hard, throttle the composer for the NEXT section.
                        const collisionRatio = result.stats.collisionCount / Math.max(1, result.stats.totalChecks);
                        if (collisionRatio > 0.2) { // > 20% of time steps were muddy
                            console.warn(`[GenerationPipeline] High Mud Detected (${(collisionRatio*100).toFixed(1)}%). Throttling density for subsequent sections.`);
                            // Throttle Global Density Scalar
                            if (context.config && context.config.vectors) {
                                context.config.vectors.rhythmicDensity *= 0.8; // Reduce by 20%
                                context.config.vectors.melodicDensity *= 0.8;
                            }
                        }
                    } else {
                        abstractTracks = result;
                    }
                } else {
                    // console.log(`[GenerationPipeline] Bypassing Vertical Slotting for Wall of Sound (Intensity: ${currentIntensity.toFixed(2)})`);
                }

                abstractTracks = this.stepNegativeSpace(abstractTracks, context);
            } catch (e) { console.warn(e); }

            // 4. Dialogue Analysis (Incremental)
            context.dialogueManager.analyzeTracks(abstractTracks, allocationMap);

            // 5. Event Transformation
            Object.entries(allocationMap).forEach(([role, assignments]) => {
                const abstractEvents = abstractTracks[role] || [];
                if (assignments.length > 20) assignments.length = 20;

                const avgTension = sectionChords.reduce((acc, c) => acc + (c.tension || 0), 0) / sectionChords.length;

                assignments.forEach((assignment, assignIdx) => {
                    if (avgTension < 0.3 && assignIdx > 0 && role === ABSTRACT_ROLES.HARMONY) return;
                    if (abstractEvents.length === 0) return;

                    // Operation Jailbreak: Inject Tessitura into context
                    const instrumentContext = {
                        ...context,
                        tessitura: assignment.instrument.tessitura
                    };

                    let instrumentEvents = adaptEvents(abstractEvents, assignment, instrumentContext, role);

                    // Apply Instrument Specifics (Doubling, Tuning, etc.)
                    instrumentEvents = applyInstrumentBehaviors(instrumentEvents, assignment, role, sectionChords);

                    // Tension/Velocity Mapping
                    let chordIndex = 0;
                    instrumentEvents = instrumentEvents.map(ev => {
                        while (chordIndex < sectionChords.length - 1 && sectionChords[chordIndex + 1].barIndex * 4 <= ev.s) {
                            chordIndex++;
                        }
                        const chord = sectionChords[chordIndex];
                        let tension = chord ? (chord.tension || 0) : 0;
                        let vel = ev.vel || 0.7;

                        if (chord && tension > 0.3) {
                            vel = Math.min(1.0, vel + ((tension - 0.3) * 0.4));
                        }
                        return { ...ev, vel, tension };
                    });

                    // Decoration
                    const ornamentStyle = harmonyTraits ? harmonyTraits.ornamentationStyle : 'clean';
                    const isBaroque = profile.melodyStrategy === 'counterpoint' || (harmonyTraits.name && harmonyTraits.name.toLowerCase().includes('baroque'));
                    const isFantasy = harmonyTraits.name && (harmonyTraits.name.toLowerCase().includes('fantasy') || harmonyTraits.name.toLowerCase().includes('romantic'));

                    if ((isBaroque || isFantasy || ornamentStyle !== 'clean') && role === ABSTRACT_ROLES.MELODY) {
                        instrumentEvents = decorateMelody(instrumentEvents, harmonyTraits.name.toLowerCase(), this.random, ornamentStyle);
                    }

                    const velStrategy = rhythmTraits ? rhythmTraits.velocityStrategy : null;
                    if (velStrategy) {
                        instrumentEvents = applyVelocityStrategy(instrumentEvents, velStrategy, context);
                    }

                    // Humanization
                    if (assignment.instrument.category === 'Orchestral') {
                        if (context.config && context.config.orchestration && context.config.orchestration.dynamicHairpins) {
                            instrumentEvents = applyHairpinPhrasing(instrumentEvents);
                        } else {
                            instrumentEvents = applyPhraseShaping(instrumentEvents, 'arch');
                        }
                        instrumentEvents = applyBreathConstraints(instrumentEvents, assignment.instrument.id);
                        instrumentEvents = applyPhrasing(instrumentEvents);
                        instrumentEvents = applyKeyswitches(instrumentEvents, assignment.instrument);
                    }

                    instrumentEvents = applyGenreHumanization(instrumentEvents, assignment.instrument, rhythmTraits, this.random, context);
                    instrumentEvents = applyExpressionCurves(instrumentEvents, context);

                    if (role === ABSTRACT_ROLES.MELODY && context.config && context.config.orchestration && context.config.orchestration.agogicAccents) {
                        instrumentEvents = applyAgogicAccents(instrumentEvents);
                    }

                    allEvents.push(...instrumentEvents);
                });
            });

            // Update barOffset for next section
            barOffset += (section.bars || 4);
        }

        this.generateGlobalOverlays(allEvents, voicedChords, context, profile, ensembleKey);

        return allEvents;
    }

    /**
     * Operation "Smart Lungs": Contextual Breathing Enforcer.
     * Replaces random crowd control with a rotational muting system.
     */
    stepBreathingEnforcer(abstractTracks, context) {
        // Priority Order (Low to High Integrity)
        // We sacrifice these first
        const PRIORITY_ORDER = [
            ABSTRACT_ROLES.HARMONY_COLOR,
            ABSTRACT_ROLES.HARMONY_RHYTHMIC,
            ABSTRACT_ROLES.COUNTER_MELODY,
            ABSTRACT_ROLES.HARMONY_PAD,
            ABSTRACT_ROLES.PERC_MID,
            ABSTRACT_ROLES.PERC_HIGH
        ];

        // Determine loop bounds
        let maxBar = 0;
        Object.values(abstractTracks).forEach(track => {
            if (track.length > 0) {
                const last = track[track.length-1];
                const bar = Math.ceil((last.s + last.len) / 4);
                if (bar > maxBar) maxBar = bar;
            }
        });

        let lastMutedRole = null;

        // Iterate per bar
        for(let bar = 0; bar < maxBar; bar++) {
            // Check Intensity
            // Note: intensityCurve might be global or section-based. Assuming global indexing via barOffset if present,
            // but here we are in section context. The context.intensityCurve usually maps to the whole structure.
            // We need to map local bar index to global index if this is a section pass.
            // Since stepOrchestration iterates sections, `context` passed here is `sectionContext`.
            // But we don't easily have the global offset here unless we pass it.
            // Fallback: Use context.intensityCurve[bar] assuming it was sliced or we check structure.
            // Actually `context.intensityCurve` is the GLOBAL curve.
            // We need to know which section we are in.
            // `stepOrchestration` has `barOffset`. It doesn't pass it to `stepActivityMasking` or here.
            // FIX: We can approximate intensity from the event timestamps relative to structure?
            // Or simpler: Just rely on `context.section` type.

            const isHighIntensity = (context.section === 'CLIMAX' || context.section === 'DROP' || context.section === 'SOLO');

            // If high intensity, SKIP breathing (Wall of Sound)
            if (isHighIntensity) {
                lastMutedRole = null; // Reset
                continue;
            }

            // Check Crowding
            const activeRoles = [];
            Object.keys(abstractTracks).forEach(role => {
                // Check if role has events in this bar
                const hasEvents = abstractTracks[role].some(ev => {
                    const evBar = Math.floor(ev.s / 4);
                    return evBar === bar;
                });
                if (hasEvents) activeRoles.push(role);
            });

            // Threshold: > 3 active roles triggers enforcement
            if (activeRoles.length > 3) {
                // Select Candidate
                let candidate = null;

                // Find lowest priority role that is active
                for (const role of PRIORITY_ORDER) {
                    if (activeRoles.includes(role)) {
                        // Constraint: Not same as last bar
                        if (role !== lastMutedRole) {
                            candidate = role;
                            break;
                        }
                    }
                }

                // If candidate found, mute it
                if (candidate) {
                    // console.log(`[BreathingEnforcer] Muting ${candidate} at Bar ${bar} (Crowded: ${activeRoles.length})`);
                    abstractTracks[candidate] = abstractTracks[candidate].filter(ev => {
                        const evBar = Math.floor(ev.s / 4);
                        return evBar !== bar;
                    });
                    lastMutedRole = candidate;
                } else {
                    // If all candidates were either not present or was last muted (and no other choice?)
                    // If we only have 1 candidate type active (e.g. Rhythm) and we muted it last bar,
                    // we are forced to play it this bar (toggle). This is acceptable.
                    lastMutedRole = null;
                }
            } else {
                lastMutedRole = null;
            }
        }

        return abstractTracks;
    }

    applySpecialEnsembles(harmonyTraits, context, allocationMap) {
        // Data-Driven Special Ensembles
        if (harmonyTraits && harmonyTraits.forcedRoles) {
            harmonyTraits.forcedRoles.forEach(forced => {
                const { instrumentId, role, strategy, ifMissing } = forced;

                // Find Candidate Instruments
                // Try exact match first, then partial include
                let candidate = context.instruments.find(i => i.id === instrumentId);
                if (!candidate) {
                    candidate = context.instruments.find(i => i.id.includes(instrumentId));
                }

                // Auto-Inject if missing and requested
                if (!candidate && ifMissing) {
                    const newInst = getInstrumentById(instrumentId);
                    if (newInst) {
                        console.log(`[GenerationPipeline] Injecting missing instrument: ${instrumentId}`);
                        // Assign a unique instance ID to ensure tracking works
                        const injected = { ...newInst, instanceId: `${newInst.id}_forced_${Date.now()}` };
                        context.instruments.push(injected);
                        candidate = injected;
                    } else {
                        console.warn(`[GenerationPipeline] Failed to inject ${instrumentId}: Not found in library.`);
                    }
                }

                if (candidate) {
                    // Ensure role bucket exists
                    if (!allocationMap[role]) allocationMap[role] = [];

                    // Check if already assigned
                    const existingAssignment = allocationMap[role].find(a => a.instrument.instanceId === candidate.instanceId);

                    if (existingAssignment) {
                        // Update strategy if forced
                        if (strategy) existingAssignment.strategy = strategy;
                    } else {
                        // Force insert at front (High Priority)
                        allocationMap[role].unshift({
                            instrument: candidate,
                            role: role,
                            strategy: strategy || 'section_aware',
                            forced: true // Flag for bypass logic
                        });
                        console.log(`[GenerationPipeline] Forced ${candidate.id} into ${role}`);
                    }
                }
            });
        }
    }

    generateGlobalOverlays(allEvents, voicedChords, context, profile, ensembleKey) {
        // Harp & Percussion Special Handling
        if (ensembleKey === 'orchestra') {
             const harpInst = context.instruments.find(i => i.id === 'harp');
             if (harpInst) {
                 let lastGlissandoSectionIndex = -1; // Cooldown tracking

                 context.structure.forEach((sect, idx) => {
                     if (idx === 0) return;

                     // Task 1: "Harp Discipline" (Weighted Probability)
                     const tension = (context.intensityCurve && context.intensityCurve[idx]) || 0.5;
                     const density = profile.rhythmicDensity !== undefined ? profile.rhythmicDensity : 0.5;
                     const probability = 0.0 + (tension * 0.5) + (density * 0.5);

                     // Cooldown: Prevent consecutive glissandos unless extreme tension
                     const isCool = (idx - lastGlissandoSectionIndex) > 1;
                     const forceTrigger = tension > 0.9;

                     if ((this.random.bool(probability) && isCool) || forceTrigger) {
                         let barTotal = 0;
                         for(let i=0; i<idx; i++) barTotal += context.structure[i].bars;
                         const glissStart = (barTotal - 1) * 4;

                         // Task 3: Integration (Pass Context)
                         const glissContext = {
                             tension: tension,
                             tempo: context.bpm || 120,
                             random: this.random
                         };

                         const glissEvents = generateHarpPattern(context.rootKey || 'C', context.scaleType || 'major', glissStart, 4.0, 'up', glissContext);
                         const adaptedGliss = glissEvents.map(e => ({
                             ...e,
                             instrumentId: 'harp',
                             instanceId: harpInst.instanceId, // Fix: Route to existing ChannelStrip
                             channel: harpInst.midiChannel,
                             role: 'decoration'
                         }));
                         allEvents.push(...adaptedGliss);
                         lastGlissandoSectionIndex = idx;
                     }
                 });
             }
             const percInst = context.instruments.find(i => i.id === 'orchestral_percussion');
             const timpInst = context.instruments.find(i => i.id === 'timpani');
             context.structure.forEach((sect, idx) => {
                 let barTotal = 0;
                 for(let i=0; i<idx; i++) barTotal += context.structure[i].bars;
                 const type = sect.type.toUpperCase();
                 if ((type.includes('CLIMAX') || type.includes('CHORUS')) && percInst) {
                      const crash = generateCymbalCrash(barTotal * 4);
                      allEvents.push({ ...crash, instrumentId: percInst.id, channel: percInst.midiChannel, role: 'percussion' });
                 }
                 if (type.includes('BUILD') && timpInst) {
                      const rollStart = (barTotal + sect.bars - 2) * 4;
                      if (rollStart >= 0) {
                          const chord = voicedChords.find(c => c.barIndex * 4 <= rollStart && (c.barIndex * 4 + c.duration) > rollStart) || voicedChords[0];
                          const rootPitch = chord ? chord.rootPitchClass : 0;
                          const roll = generateTimpaniRoll(rollStart, 8.0, rootPitch);
                          // Fix: Map array of events instead of spreading array into object
                          const mappedRoll = roll.map(ev => ({ ...ev, instrumentId: 'timpani', channel: timpInst.midiChannel, role: 'percussion' }));
                          allEvents.push(...mappedRoll);
                      }
                 }
             });
        }
    }

    validateAndRepairChords(voicedChords, context) {
        if (!voicedChords) return;
        voicedChords.forEach((chord, idx) => {
            let repaired = false;

            // 1. Repair Root Pitch Class
            if (chord.rootPitchClass === undefined || chord.rootPitchClass === null || isNaN(chord.rootPitchClass)) {
                // Fallback to previous chord's root or Key Center
                const prev = voicedChords[idx - 1];
                if (prev && typeof prev.rootPitchClass === 'number') {
                    chord.rootPitchClass = prev.rootPitchClass;
                } else {
                    // Fallback to key
                    const rootKey = context.rootKey || 'C';
                    chord.rootPitchClass = NOTES.indexOf(normalizeKey(rootKey));
                    if (chord.rootPitchClass === -1) chord.rootPitchClass = 0; // Absolute safety
                }
                repaired = true;
            }

            // 2. Repair Notes (Ghost Chord Check)
            if (!chord.notes || chord.notes.length === 0 || chord.notes.some(n => isNaN(n))) {
                // Generate Emergency Triad based on (possibly repaired) root
                const root = chord.rootPitchClass;
                const isMinor = chord.quality === 'minor';
                const isDim = chord.quality === 'diminished';

                // Basic Triad in middle octave
                chord.notes = [
                    root + 60,
                    root + (isMinor || isDim ? 63 : 64),
                    root + (isDim ? 66 : 67)
                ];
                repaired = true;
            }

            if (repaired) {
                console.warn(`[GenerationPipeline] Ghost Chord detected and repaired at Bar ${chord.barIndex}`);
            }
        });
    }

    stepPostProcessing(allEvents, voicedChords, context, profile) {
        if (context.scaleType === 'quartertone') {
             const rootKey = context.rootKey || 'C';
             const rootIdx = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"].indexOf(rootKey);
             allEvents.forEach(ev => {
                 if (ev.d !== undefined) {
                     const notePC = ev.d % 12;
                     const semitonesFromRoot = (notePC - rootIdx + 12) % 12;
                     if (semitonesFromRoot === 2 || semitonesFromRoot === 9 || semitonesFromRoot === 8) {
                         ev.detune = -50;
                     } else {
                         ev.detune = 0;
                     }
                 }
             });
        }

        allEvents = this.engine.filterRepetition(allEvents, context.scaleType);
        if (context.structure) {
            allEvents = this.engine.applySectionDynamics(allEvents, context.structure, context.timeSig || 4, context.traits);
        }

        const timeSignature = context.timeSig || 4;
        allEvents = enforceHarmonicContext(allEvents, voicedChords, timeSignature);

        // cleanOverlaps might truncate notes that cross bar lines (suspensions).
        // Phase 2: Melodic Phrasing & Anacrusis requires preservation of overlapping notes.
        // We will call cleanOverlaps but verify it doesn't strictly cut at section boundaries if it shouldn't.
        // For now, cleanOverlaps in MusicEngine is basic.
        // Ideally, we should perform a "Smart Cleanup" that allows monophonic overlap if intentional (legato).

        // --- Round 5 Improvement: Legato Pad Glue ---
        // Merge abutting pad notes BEFORE cleaning overlaps, to form long tones.
        try {
            allEvents = applyLegatoGlue(allEvents);
        } catch (e) {
            console.warn('[GenerationPipeline] applyLegatoGlue failed', e);
        }

        // --- Task 3: Unison Culling (Polyphony Optimization) ---
        // Remove redundant unison notes to save CPU
        try {
            allEvents = cullUnisonNotes(allEvents);
        } catch (e) {
            console.warn('[GenerationPipeline] cullUnisonNotes failed', e);
        }

        // Fix: "Voice Stealing" Safety Valve
        // Prevent CPU overload from overlapping clouds (Polyphony Explosion)
        try {
            allEvents = capPolyphony(allEvents, 8); // Cap at 8 voices per track
        } catch (e) {
            console.warn('[GenerationPipeline] capPolyphony failed', e);
        }

        // Task 2: The Sanitization Layer (Guardrails)
        // Strict cleanup of overlaps, durations, and boundaries
        allEvents = this.stepSanitization(allEvents, context);

        // --- NEW: Duration Physics (The "Humanizer") ---
        // Applies acoustic release, resonance, and articulation-based length adjustments
        try {
            allEvents = applyGlobalPhysics(allEvents, context);
        } catch (e) {
            console.warn('[GenerationPipeline] applyGlobalPhysics failed', e);
        }

        // Pass context for instrument resolution in dynamic legato
        allEvents = this.engine.cleanOverlaps(allEvents, context);

        return allEvents;
    }

    /**
     * Operation State Hygiene: The "Hard Reset"
     * Clears all session-specific data to prevent Genre Leakage.
     */
    resetSession(context, profile) {
        // Clear Derived State (Zombie Arrays)
        context.kickPatternEvents = null;
        context.barInstrumentation = null;
        context.intensityCurve = null;
        context.harmonyState = null;
        context.spectrumRegistry = null;

        // Clear Persistent Caches
        context.cache = {};

        // Clear Song Memory (unless explicitly requested to persist, e.g. for Multi-Movement works)
        if (!context.keepSongMemory) {
            context.songMemory = null;
        }

        // Task 3: Instrument State Locking (The "Anchor")
        // Only clear instruments if the Genre has changed OR explicit lock is off.
        // If Genre matches last run, we preserve the Ensemble to stop "Thrashing".
        const genreChanged = (profile && this.lastHarmonySource !== profile.harmonySource);

        if (!context.lockInstruments && genreChanged) {
            console.log('[GenerationPipeline] Genre changed. Resetting Ensemble.');
            context.instruments = [];
        }
    }

    /**
     * Task 2: Sanitization Layer
     * Enforces hard limits on event duration and boundaries to prevent "Wall of Sound"
     */
    stepSanitization(events, context) {
        if (!events || events.length === 0) return [];
        const sanitized = [];

        // Track boundaries per section?
        // Simpler: Just ensure no event has infinite or NaN duration
        // And optionally clip to next bar start if strict?
        // For now, rely on cleanOverlaps for monophonic collision.
        // This function handles Logic Errors (NaN, Infinite, Negative).

        events.forEach(ev => {
            // 1. Validate Time/Duration
            if (typeof ev.s !== 'number' || isNaN(ev.s)) return; // Drop invalid time
            if (typeof ev.len !== 'number' || isNaN(ev.len)) ev.len = 0.25; // Default short

            // 2. Clip Negative/Zero Duration
            if (ev.len <= 0) ev.len = 0.1;

            // 3. Cap Max Duration (Safety)
            // No note should play for > 8 bars unless drone
            if (ev.len > 32) ev.len = 32;

            // 4. Prune Micro-Overlaps (Prevent glitchy retriggering)
            // Handled mostly by cleanOverlaps, but let's ensure minimum gap?
            // Skipped for now, cleanOverlaps is robust.

            // 5. Bounds Check (Song End)
            // If we knew total song length... context.structure
            let totalBars = 0;
            if (context.structure) {
                context.structure.forEach(s => totalBars += (s.bars || 4));
            }
            if (totalBars > 0 && ev.s >= totalBars * 4) {
                // Drop event after song end
                return;
            }
            // Clip duration at song end
            if (totalBars > 0 && (ev.s + ev.len) > totalBars * 4) {
                ev.len = (totalBars * 4) - ev.s;
            }

            sanitized.push(ev);
        });

        return sanitized;
    }
}
